import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Avatar,
  Container,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import { Apple, Android, Restaurant } from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface MenuCategory {
  id: string;
  name: string;
  order: number;
}

interface MenuSize {
  id: string;
  name: string;
  priceModifier: number;
  isDefault: boolean;
}

interface MenuExtra {
  id: string;
  name: string;
  price: number;
  categoryIds: string[];
  isAvailable: boolean;
}

interface MenuItem {
  id: string;
  categoryId: string;
  name: string;
  description: string;
  basePrice: number;
  price?: number;
  photoURL?: string;
  isAvailable: boolean;
  hasSizes: boolean;
  hasExtras: boolean;
}

interface CafeInfo {
  id: string;
  name: string;
  address?: string;
  logoURL?: string;
  themeColor?: string;
  splashURL?: string | null;
  splashEnabled?: boolean;
}

interface MenuData {
  cafe: CafeInfo;
  categories: MenuCategory[];
  items: MenuItem[];
  sizes: MenuSize[];
  extras: MenuExtra[];
}

const MenuPage: React.FC = () => {
  const { cafeId } = useParams<{ cafeId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [menuData, setMenuData] = useState<MenuData | null>(null);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [splashOpen, setSplashOpen] = useState(false);

  // Theme color - default to coffee brown
  const themeColor = menuData?.cafe?.themeColor || '#8B4513';

  useEffect(() => {
    const fetchMenu = async () => {
      if (!cafeId) return;

      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/public/menu/${cafeId}`);
        setMenuData(response.data.data); // Backend returns {success: true, data: {...}}
        setError(null);
      } catch (err: any) {
        console.error('Menu fetch error:', err);
        if (err.response?.status === 404) {
          setError('Bu kafe bulunamadi veya menusu yok.');
        } else {
          setError('Menu yuklenirken bir hata olustu.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [cafeId]);

  // Splash gösterim mantığı - Her sayfa yüklenmesinde gösterir
  useEffect(() => {
    if (!menuData || !cafeId) return;
    const { splashEnabled, splashURL } = menuData.cafe;
    if (splashEnabled && splashURL) {
      setSplashOpen(true);
    }
  }, [menuData, cafeId]);

  const handleCloseSplash = () => setSplashOpen(false);

  // Get items for current category
  const currentCategoryId = menuData?.categories[selectedCategory]?.id;
  const categoryItems = menuData?.items.filter(item => item.categoryId === currentCategoryId) || [];

  // Get extras for current category
  const categoryExtras = menuData?.extras.filter(
    extra => extra.categoryIds.length === 0 || extra.categoryIds.includes(currentCategoryId || '')
  ) || [];

  // Handle item click
  const handleItemClick = (item: MenuItem) => {
    setSelectedItem(item);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedItem(null);
  };

  // Get default size price modifier
  const getDefaultSizePriceText = (item: MenuItem) => {
    if (!item.hasSizes || !menuData?.sizes.length) {
      return `${(item.basePrice || item.price || 0).toFixed(2)} TL`;
    }
    const defaultSize = menuData.sizes.find(s => s.isDefault) || menuData.sizes[0];
    const price = (item.basePrice || item.price || 0) + (defaultSize?.priceModifier || 0);
    return `${price.toFixed(2)} TL`;
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: themeColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Pulsing Background Layers */}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            background: `radial-gradient(circle at center, ${themeColor} 0%, ${themeColor}DD 100%)`,
            zIndex: 1
          }}
        />

        {/* The "Pulse" element */}
        <Box
          sx={{
            position: 'absolute',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: `radial-gradient(circle at center, rgba(255,255,255,0.4) 0%, transparent 70%)`,
            zIndex: 2,
            animation: 'pulse-bg 1.5s infinite ease-out',
            '@keyframes pulse-bg': {
              '0%': { transform: 'scale(0.5)', opacity: 0 },
              '10%': { opacity: 0.8 },
              '30%': { transform: 'scale(1.5)', opacity: 0 },
              '40%': { transform: 'scale(0.5)', opacity: 0 },
              '50%': { opacity: 0.5 },
              '70%': { transform: 'scale(1.2)', opacity: 0 },
              '100%': { transform: 'scale(1.2)', opacity: 0 }
            }
          }}
        />

      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Restaurant sx={{ fontSize: 80, color: 'white', mb: 2, opacity: 0.8 }} />
          <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>
            {error}
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', mb: 4 }}>
            SobCof uygulamasini indirerek kafeleri kesfedin!
          </Typography>
          <DownloadButtons />
        </Box>
      </Box>
    );
  }

  if (!menuData) return null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#f8f9fa',
        pb: 12
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}DD 100%)`,
          pt: 4,
          pb: 6,
          textAlign: 'center',
          position: 'relative'
        }}
      >
        {/* Cafe Logo */}
        {menuData.cafe.logoURL ? (
          <Avatar
            src={menuData.cafe.logoURL}
            alt={menuData.cafe.name}
            sx={{
              width: 100,
              height: 100,
              mx: 'auto',
              mb: 2,
              border: '4px solid white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
          />
        ) : (
          <Avatar
            sx={{
              width: 100,
              height: 100,
              mx: 'auto',
              mb: 2,
              bgcolor: 'white',
              color: themeColor,
              fontSize: 40,
              fontWeight: 700,
              border: '4px solid white',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
            }}
          >
            {menuData.cafe.name.charAt(0)}
          </Avatar>
        )}

        {/* Cafe Name */}
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 700,
            mb: 1,
            textShadow: '0 2px 10px rgba(0,0,0,0.2)'
          }}
        >
          {menuData.cafe.name}
        </Typography>
      </Box>

      {/* Category Tabs */}
      <Container maxWidth="sm" sx={{ mt: -3 }}>
        <Card
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Tabs
            value={selectedCategory}
            onChange={(_, newValue) => setSelectedCategory(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            centered={menuData.categories.length <= 3}
            sx={{
              bgcolor: 'white',
              px: 0,
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                minHeight: 64,
                minWidth: 100,
                px: 3,
                pt: 4,
                pb: 0
              },
              '& .Mui-selected': {
                color: `${themeColor} !important`
              },
              '& .MuiTabs-indicator': {
                backgroundColor: themeColor,
                height: 3
              },
              '& .MuiTabs-scrollButtons': {
                color: themeColor,
                pt: 4,
                pb: 0,
                minWidth: 32,
                width: 32,
                mx: -0.5,
                '&.Mui-disabled': {
                  opacity: 0.3
                }
              }
            }}
          >
            {menuData.categories.map((category, index) => (
              <Tab key={category.id} label={category.name} />
            ))}
          </Tabs>
        </Card>
      </Container>

      {/* Menu Items */}
      <Container maxWidth="sm" sx={{ mt: 3 }}>
        {categoryItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Restaurant sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="body1" color="text.secondary">
              Bu kategoride urun bulunmuyor.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={2}>
            {categoryItems.map((item) => (
              <MenuItemCard
                key={item.id}
                item={item}
                sizes={menuData.sizes}
                extras={categoryExtras}
                themeColor={themeColor}
                getPriceText={() => getDefaultSizePriceText(item)}
                onClick={() => handleItemClick(item)}
              />
            ))}
          </Stack>
        )}
      </Container>

      {/* Kafe Tanıtım Görseli (Splash) Dialog */}
      <Dialog
        open={splashOpen}
        onClose={handleCloseSplash}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            background: '#000'
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          {/* Kapat Butonu */}
          <IconButton
            onClick={handleCloseSplash}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 10,
              bgcolor: 'rgba(0,0,0,0.55)',
              color: 'white',
              backdropFilter: 'blur(4px)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' }
            }}
          >
            <Typography sx={{ fontSize: 18, lineHeight: 1, px: 0.5 }}>✕</Typography>
          </IconButton>

          {/* Splash Görseli */}
          {menuData?.cafe?.splashURL && (
            <Box
              component="img"
              src={menuData.cafe.splashURL}
              alt="Kafe tanıtım görseli"
              sx={{
                width: '100%',
                maxHeight: '80vh',
                objectFit: 'contain',
                display: 'block'
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* Item Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        {selectedItem && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedItem.name}
              </Typography>
              {selectedItem.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedItem.description}
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              {/* Item Image */}
              {selectedItem.photoURL && (
                <Box
                  component="img"
                  src={selectedItem.photoURL}
                  alt={selectedItem.name}
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 2,
                    mb: 2
                  }}
                />
              )}

              {/* Size Options */}
              {selectedItem.hasSizes && menuData?.sizes && menuData.sizes.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: themeColor }}>
                    Boyut Seçenekleri
                  </Typography>
                  <Stack spacing={1}>
                    {menuData.sizes.map(size => {
                      const price = (selectedItem.basePrice || selectedItem.price || 0) + size.priceModifier;
                      return (
                        <Box
                          key={size.id}
                          sx={{
                            p: 2,
                            border: `2px solid ${size.isDefault ? themeColor : '#e0e0e0'}`,
                            borderRadius: 2,
                            bgcolor: size.isDefault ? `${themeColor}10` : 'transparent',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {size.name}
                              {size.isDefault && (
                                <Chip
                                  label="Varsayılan"
                                  size="small"
                                  sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                />
                              )}
                            </Typography>
                          </Box>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: themeColor }}>
                            {price.toFixed(2)} TL
                          </Typography>
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              )}

              {/* Extra Options */}
              {selectedItem.hasExtras && categoryExtras.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: themeColor }}>
                    Ekstra Seçenekler
                  </Typography>
                  <Stack spacing={1}>
                    {categoryExtras.filter(e => e.isAvailable).map(extra => (
                      <Box
                        key={extra.id}
                        sx={{
                          p: 2,
                          border: '2px solid #e0e0e0',
                          borderRadius: 2,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {extra.name}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                          +{extra.price.toFixed(2)} TL
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* No options message */}
              {!selectedItem.hasSizes && !selectedItem.hasExtras && (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: themeColor, mb: 1 }}>
                    {getDefaultSizePriceText(selectedItem)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Bu ürün için ek seçenek bulunmamaktadır.
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDialog} variant="contained" fullWidth sx={{ bgcolor: themeColor, '&:hover': { bgcolor: themeColor } }}>
                Kapat
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

// Menu Item Card Component
interface MenuItemCardProps {
  item: MenuItem;
  sizes: MenuSize[];
  extras: MenuExtra[];
  themeColor: string;
  getPriceText: () => string;
  onClick: () => void;
}

const MenuItemCard: React.FC<MenuItemCardProps> = ({ item, sizes, extras, themeColor, getPriceText, onClick }) => {
  return (
    <Card
      elevation={1}
      onClick={onClick}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        opacity: item.isAvailable ? 1 : 0.7,
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
    >
      <Box sx={{ display: 'flex' }}>
        {/* Image */}
        {item.photoURL && (
          <CardMedia
            component="img"
            image={item.photoURL}
            alt={item.name}
            sx={{
              width: 100,
              alignSelf: 'stretch',
              objectFit: 'cover'
            }}
          />
        )}

        {/* Content */}
        <CardContent sx={{ flex: 1, py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, pr: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {item.name}
              </Typography>
              {item.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {item.description}
                </Typography>
              )}
            </Box>

            {/* Price */}
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: themeColor,
                  whiteSpace: 'nowrap'
                }}
              >
                {getPriceText()}
              </Typography>
            </Box>
          </Box>

          {/* Tags */}
          <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            {!item.isAvailable && (
              <Chip
                label="Tukendi"
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  bgcolor: '#ffebee',
                  color: '#c62828',
                  fontWeight: 600
                }}
              />
            )}
            {item.hasSizes && (
              <Chip
                label="S/M/L"
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  borderColor: themeColor,
                  color: themeColor
                }}
              />
            )}
            {item.hasExtras && (
              <Chip
                label="+Ekstra"
                size="small"
                variant="outlined"
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  borderColor: '#4CAF50',
                  color: '#4CAF50'
                }}
              />
            )}
          </Stack>
        </CardContent>
      </Box>
    </Card>
  );
};

// Download Buttons Component
const DownloadButtons: React.FC = () => (
  <Stack direction="row" spacing={2} justifyContent="center">
    <IconButton
      href="https://apps.apple.com/tr/app/sobcof/id6751366978?l=tr"
      target="_blank"
      sx={{
        bgcolor: 'white',
        color: '#000',
        width: 56,
        height: 56,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
      }}
    >
      <Apple sx={{ fontSize: 32 }} />
    </IconButton>
    <IconButton
      href="https://play.google.com/store/apps/details?id=com.sobcof.sobcofandroid"
      target="_blank"
      sx={{
        bgcolor: 'white',
        color: '#3DDC84',
        width: 56,
        height: 56,
        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
      }}
    >
      <Android sx={{ fontSize: 32 }} />
    </IconButton>
  </Stack>
);

export default MenuPage;
